import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Users, Lock, Lightbulb } from "lucide-react";
import api from "@/lib/api";

/**
 * SocialPatternsCard — Laura Piccinini's curated social-context patterns
 * (June 2026). Identical engine to WorkPatternsCard but parametric on the
 * `context` prop (famiglia / amici / partner / solo / ...).
 *
 * Backend: GET /api/voice-mood/social-patterns/{context}
 *   - Returns up to 2 patterns that exceed thresholds (N≥4, ≥20%)
 *   - Each pattern includes interpretation + ONE weekly-rotated advice
 *
 * Beta testers see only narrative wording. Clinicians additionally see the
 * shorthand (e.g. "C↑ Ct↓ E↑ P↓"), N count, and percentage.
 */
const SkeletonCard = ({ title }) => (
  <Card className="border" data-testid="social-patterns-loading">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-500" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-full" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-5/6" />
      </div>
    </CardContent>
  </Card>
);

const MATURITY_LEAD_IN = {
  emerging:  "Sta emergendo un pattern, ancora da confermare nel tempo.",
  recurring: "Si conferma un pattern ricorrente.",
  stable:    "Si conferma in modo stabile un pattern.",
};

export const SocialPatternsCard = ({ context = "famiglia", isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/voice-mood/social-patterns/${context}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [context]);

  const fallbackLabel = {
    famiglia: "Famiglia", amici: "Amici", partner: "Partner",
    solo: "Da solo", colleghi: "Colleghi",
  }[context] || context;
  const contextLabel = data?.context_label || fallbackLabel;
  const isSoloCtx = context === "solo";
  const title = isClinician
    ? (isSoloCtx ? "Pattern · Da solo" : `Pattern relazionali · ${contextLabel}`)
    : (isSoloCtx ? "Come vivi i momenti da solo" : `Come vivi i momenti ${contextLabel.toLowerCase()}`);

  if (loading) return <SkeletonCard title={title} />;
  if (!data) return null;

  // Context known but not yet configured (e.g. amici/partner/solo for now)
  if (data.not_configured) {
    if (!isClinician) return null;
    return (
      <Card className="border" data-testid={`social-patterns-${context}-card`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-600" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground italic">
            [Clinico] Configurazioni cliniche per "{contextLabel}" non ancora definite.
          </p>
        </CardContent>
      </Card>
    );
  }

  const subtitle = isClinician
    ? `Configurazioni curate Piccinini 2026 sul contesto "${contextLabel}" (ultimi 30 giorni)`
    : isSoloCtx
      ? "Come stai vivendo i tuoi momenti da solo nelle ultime settimane."
      : `Come si stanno organizzando i tuoi momenti con la ${contextLabel.toLowerCase()} nelle ultime settimane.`;

  const lockedText = isSoloCtx
    ? `Servono almeno ${data.min_n_threshold} check-in "Da solo" negli ultimi 30 giorni per leggere i pattern. Attualmente: ${data.n_context_total}.`
    : `Servono almeno ${data.min_n_threshold} check-in nel contesto "${contextLabel}" negli ultimi 30 giorni per leggere i pattern. Attualmente: ${data.n_context_total}.`;

  const emptyText = isSoloCtx
    ? "I tuoi momenti da solo nelle ultime settimane non disegnano ancora un pattern netto. Continuando con i check-in, i pattern emergeranno con più chiarezza."
    : `I tuoi momenti con la ${contextLabel.toLowerCase()} nelle ultime settimane non disegnano ancora un pattern netto. Continuando con i check-in, i pattern emergeranno con più chiarezza.`;

  return (
    <Card className="border" data-testid={`social-patterns-${context}-card`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-600" />
          {title}
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">{subtitle}</p>
      </CardHeader>

      <CardContent>
        {!data.unlocked ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground" data-testid={`social-patterns-${context}-locked`}>
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{lockedText}</span>
          </div>
        ) : data.patterns.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid={`social-patterns-${context}-empty`}>
            {emptyText}
          </p>
        ) : (
          <div className="space-y-5" data-testid={`social-patterns-${context}-list`}>
            {data.patterns.map((p) => (
              <article
                key={p.id}
                className="border rounded-md p-3 space-y-3"
                data-testid={`social-pattern-${p.id}`}
              >
                <header className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-slate-800">{p.name}</h4>
                  {isClinician && (
                    <span className="text-[10px] text-muted-foreground tracking-wide">
                      {p.shorthand} · N={p.n} · {p.pct}%
                    </span>
                  )}
                </header>

                <p className="text-xs leading-relaxed text-foreground/90">
                  <span className="italic text-muted-foreground">
                    {MATURITY_LEAD_IN[p.maturity] || ""}
                  </span>
                  {" "}
                  {p.interpretation}
                </p>

                {p.advice && (
                  <div
                    className="bg-amber-50/60 border border-amber-200/60 rounded-md p-2.5 space-y-1"
                    data-testid={`social-pattern-${p.id}-advice`}
                  >
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-900 tracking-wide">
                      <Lightbulb className="w-3 h-3" strokeWidth={2} />
                      <span>SUGGERIMENTO DELLA SETTIMANA</span>
                    </div>
                    <p className="text-xs font-semibold text-slate-800 leading-snug">
                      {p.advice.title}
                    </p>
                    {p.advice.text && (
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        {p.advice.text}
                      </p>
                    )}
                  </div>
                )}
              </article>
            ))}

            {isClinician && (
              <p className="text-[10px] text-muted-foreground italic border-t border-dashed border-slate-200 pt-2">
                [Clinico] {data.n_context_total} check-in "{contextLabel}" ·
                {" "}{data.n_unbinned} non classificati · settimana ISO {data.iso_week} ·
                {" "}soglie: N≥{data.min_pattern_count}, ≥{data.min_pattern_pct}%
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SocialPatternsCard;
